<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'
import GalleryPicture from '@/components/organizer/galleries/GalleryPicture.vue'
import type { BulkMoveResult } from '@/composables/useGallery'
import { GalleryApiErrorCode, GalleryType } from '@/types/galleries'
import type { OrganizerGallery } from '@/types/galleries'

const props = defineProps<{
  open: boolean
  sourceGalleryId: string
  itemCount: number
  galleries: OrganizerGallery[]
  /** Moves the selected items and resolves with the server result. */
  move: (targetGalleryId: string) => Promise<BulkMoveResult>
}>()

const emit = defineEmits<{
  close: []
  moved: [targetGalleryId: string]
  'create-new': []
}>()

const { t } = useI18n()

const targetId = ref<string | null>(null)
const saving = ref(false)
const error = ref<string | null>(null)

const targets = computed(() => props.galleries.filter((g) => g.id !== props.sourceGalleryId && g.type !== GalleryType.External),
)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    targetId.value = null
    error.value = null
  },
  { immediate: true },
)

function handleClose(): void {
  if (saving.value) return
  emit('close')
}

async function handleSubmit(): Promise<void> {
  if (targetId.value === null) {
    error.value = t('galleries.move.choose')
    return
  }
  saving.value = true
  error.value = null
  try {
    const result = await props.move(targetId.value)
    if (result.ok) {
      emit('moved', targetId.value)
    } else {
      error.value =
        result.code === GalleryApiErrorCode.GalleryItemLimitReached
          ? t('galleries.move.limit')
          : t('galleries.errors.generic')
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <SlideOver :open="open" :title="t('galleries.move.title', itemCount)" :saving="saving" @close="handleClose">
    <form id="move-items-form" class="space-y-4" novalidate @submit.prevent="handleSubmit">
      <p class="text-sm text-slate-400">{{ t('galleries.move.description') }}</p>

      <p v-if="targets.length === 0" class="rounded-xl border border-slate-700 bg-slate-900 px-4 py-6 text-center text-sm text-slate-400">
        {{ t('galleries.move.no_other') }}
      </p>

      <fieldset v-else>
        <legend class="sr-only">{{ t('galleries.move.title', itemCount) }}</legend>
        <ul class="space-y-2">
          <li v-for="gallery in targets" :key="gallery.id">
            <label class="flex items-center gap-3 rounded-xl border border-slate-700 p-3 cursor-pointer has-checked:border-emerald-500 has-checked:bg-emerald-500/5">
              <input v-model="targetId" type="radio" name="move-target" :value="gallery.id" class="accent-emerald-500" />
              <span class="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-900">
                <GalleryPicture v-if="gallery.cover" :image="gallery.cover" sizes="56px" />
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-white truncate">{{ gallery.title }}</span>
                <span class="block text-xs text-slate-400">{{ t('galleries.move.items', gallery.item_count) }}</span>
              </span>
            </label>
          </li>
        </ul>
      </fieldset>

      <button
        type="button"
        class="text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
        :disabled="saving"
        @click="emit('create-new')"
      >
        {{ t('galleries.move.create_new') }}
      </button>

      <p v-if="error" class="text-xs text-red-400" role="alert">{{ error }}</p>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="saving"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          @click="handleClose"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="submit"
          form="move-items-form"
          :disabled="saving || targets.length === 0"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        >
          <AppSpinner v-if="saving" size="button" />
          {{ t('galleries.move.submit') }}
        </button>
      </div>
    </template>
  </SlideOver>
</template>
